// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ReactNode } from 'react';

import type { Template } from '../../api/types';
import { EDIT_TEMPLATES, EDIT_TEMPLATES_LABEL, NO_TEMPLATE, templateSelectAction } from '../../rapport/model';
import { Select, type SelectSize, type SelectVariant } from '../Select';
import styles from './TemplateSelect.module.css';

export interface TemplateSelectProps {
  id: string;
  label: string;
  templates: Template[];
  value: number | null;
  onChange: (id: number | null) => void;
  /** Offer "Ingen" as the first option (the Ressourcetabel picker). */
  allowNone?: boolean;
  disabled?: boolean;
  /**
   * Adds "Redigér skabeloner…" as the last option, which calls this instead
   * of changing the choice (the select stays on the current template).
   */
  onEditTemplates?: () => void;
  /** The control's look: outline/sm in forms (default); a Bygning toolbar passes filled/md. */
  variant?: SelectVariant;
  size?: SelectSize;
  /**
   * `stacked` (default): the caption over the control (Rapport). `inline`:
   * the caption inside the well, before the value (the Tabel toolbar).
   */
  layout?: 'stacked' | 'inline';
}

/** A labelled native select over the project's templates (R9). */
export function TemplateSelect({
  id,
  label,
  templates,
  value,
  onChange,
  allowNone,
  disabled,
  onEditTemplates,
  variant = 'outline',
  size = 'sm',
  layout = 'stacked',
}: TemplateSelectProps) {
  const select = (prefix?: ReactNode) => (
    <Select
      variant={variant}
      size={size}
      prefix={prefix}
      id={id}
      value={value === null ? NO_TEMPLATE : String(value)}
      disabled={disabled}
      onChange={(e) => {
        const action = templateSelectAction(e.target.value);
        if (action.kind === 'edit') onEditTemplates?.();
        else onChange(action.id);
      }}
    >
      {allowNone && <option value={NO_TEMPLATE}>Ingen</option>}
      {!allowNone && value === null && <option value={NO_TEMPLATE}>Vælg skabelon…</option>}
      {templates.map((t) => (
        <option key={t.id} value={String(t.id)}>
          {t.name}
        </option>
      ))}
      {onEditTemplates && <option value={EDIT_TEMPLATES}>{EDIT_TEMPLATES_LABEL}</option>}
    </Select>
  );

  if (layout === 'inline') {
    return select(
      <label htmlFor={id} className={styles.inlineLabel}>
        {label}
      </label>,
    );
  }
  return (
    <label className={styles.field} htmlFor={id}>
      <span className={styles.fieldLabel}>{label}</span>
      {select()}
    </label>
  );
}
